var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { call, fork, put, select } from 'redux-saga/effects';
import { INTELLIGENT_PHOTO_CAPTURE_ESTIMATOR_IMAGE_ANALYZE_REQUEST, photoCaptureDataFailureAction, photoCaptureIntelligentEstimatorImageAnalyzeFailureAction, photoCaptureIntelligentEstimatorImageAnalyzeSuccessAction } from '../actions';
import { IntelligentAssignmentSummary } from '../constants';
import { getAttachmentBatchActivityInfo, getAttachmentIterationInfo, getInstanceReferenceId, getIsLicensePlateMatched, getIsVinMatched, getIsOdometerMatched, getOCRUserVerifiedFlags, getPhotoCaptureVehicle, getPhotoCaptureCompanyCode, getPhotoCaptureCountryCode, getPhotoCaptureClaimNumber, getShouldEnableImageQualityAssessment, getShouldEnableOCRImageTextDecode } from '../selectors';
import { IntelligentPhotoCaptureEstimatorImageAnalyzeService } from '../services';
import { attachmentBatchActivityInfo, attachmentIteration, getErrorMessagesBasedOnStatusCode, getPhotoCaptureDeviceMetaInfo, getUpstreamVinStatus } from '../utilities';
import { IntelligentPhotoCaptureEstimatorCaptureFileSource, IntelligentPhotoCaptureEstimatorCaptureMode } from '../enums';
import { intelligentPhotoCaptureFailureLoggingSaga } from './intelligentPhotoCaptureFailureLoggingSaga';
/**
 * Saga to handle estimator image analyze request
 * Processes a batch of images, calls the analyze service, and dispatches success or failure actions
 * @param batch - The batch of files to analyze
 * @param batchId - The unique identifier for the batch
 * @yields Effects for Redux-Saga
 */
export function* intelligentPhotoCaptureEstimatorImageAnalyzeSaga(batch, batchId) {
    let requestPayload;
    try {
        if (batch.length === 0) {
            throw new Error(IntelligentAssignmentSummary.BATCH_FAILED_TO_UPLOAD);
        }
        const estimatorImageAnalyzeService = new IntelligentPhotoCaptureEstimatorImageAnalyzeService();
        const batchActivityInfoList = yield select(getAttachmentBatchActivityInfo);
        const batchActivityInfo = batchActivityInfoList.find((batch) => batch.batchId === batchId);
        if (!batchActivityInfo) {
            throw new Error(`Batch activity info not found for batchId: ${batchId}`);
        }
        // Get iqaOpted and ocrOpted from estimator context state
        const iqaOpted = yield select(getShouldEnableImageQualityAssessment);
        const ocrOpted = yield select(getShouldEnableOCRImageTextDecode);
        const instanceReferenceId = yield select(getInstanceReferenceId);
        const photoCaptureVehicle = yield select(getPhotoCaptureVehicle);
        const upstreamVin = (photoCaptureVehicle === null || photoCaptureVehicle === void 0 ? void 0 : photoCaptureVehicle.vinNo) || undefined;
        const countryCode = yield select(getPhotoCaptureCountryCode);
        const upstreamVinStatus = getUpstreamVinStatus(upstreamVin, countryCode, photoCaptureVehicle);
        // Fetch S3 URLs for attachments using call effect for saga middleware control.
        const attachmentsWithS3Urls = yield call(() => __awaiter(this, void 0, void 0, function* () {
            return Promise.all(batchActivityInfo.attachments.map((attachment) => __awaiter(this, void 0, void 0, function* () {
                const s3Url = yield attachmentBatchActivityInfo.gets3UrlForDocUID(attachment.docUID);
                return Object.assign(Object.assign({}, attachment), { uri: s3Url });
            })));
        }));
        // Read the latest matched state values JUST BEFORE building the request payload
        // This ensures that if multiple API calls are made, each one gets the updated state from previous calls
        const isVinMatched = yield select(getIsVinMatched);
        const isLicensePlateMatched = yield select(getIsLicensePlateMatched);
        const isOdometerMatched = yield select(getIsOdometerMatched);
        const claimNumber = yield select(getPhotoCaptureClaimNumber);
        const companyCode = yield select(getPhotoCaptureCompanyCode);
        const attachmentIterationInfo = yield select(getAttachmentIterationInfo);
        const currentIteration = attachmentIteration.getInProgressIteration(attachmentIterationInfo);
        const fileSource = (currentIteration === null || currentIteration === void 0 ? void 0 : currentIteration.captureMode) === IntelligentPhotoCaptureEstimatorCaptureMode.TAKE_PHOTOS
            ? IntelligentPhotoCaptureEstimatorCaptureFileSource.CAMERA
            : IntelligentPhotoCaptureEstimatorCaptureFileSource.GALLERY;
        const ocrUserVerifiedState = yield select(getOCRUserVerifiedFlags);
        const { isVinUserVerified, isLicensePlateUserVerified, isOdometerUserVerified } = ocrUserVerifiedState || {};
        requestPayload = Object.assign({ requestId: batchId, iqaOpted,
            ocrOpted, attachments: attachmentsWithS3Urls, vinOcrRequired: !(isVinUserVerified || isVinMatched), licensePlateOcrRequired: !(isLicensePlateUserVerified || isLicensePlateMatched), odometerOcrRequired: !(isOdometerUserVerified || isOdometerMatched), upstreamVin,
            upstreamVinStatus, referenceId: instanceReferenceId, claimNumber,
            fileSource }, getPhotoCaptureDeviceMetaInfo());
        const response = yield call({
            context: estimatorImageAnalyzeService,
            fn: estimatorImageAnalyzeService.analyzeEstimatorImages
        }, companyCode, requestPayload, instanceReferenceId);
        yield put(photoCaptureIntelligentEstimatorImageAnalyzeSuccessAction(response));
    }
    catch (error) {
        const errorMessage = getErrorMessagesBasedOnStatusCode(error);
        // Fire-and-forget error logging so catch-path handling is not blocked.
        yield fork(intelligentPhotoCaptureFailureLoggingSaga, {
            actionType: INTELLIGENT_PHOTO_CAPTURE_ESTIMATOR_IMAGE_ANALYZE_REQUEST,
            response: error,
            requestedPayload: requestPayload,
            errorDetail: errorMessage
        });
        yield put(photoCaptureDataFailureAction(errorMessage));
        yield put(photoCaptureIntelligentEstimatorImageAnalyzeFailureAction(batchId));
    }
}
//# sourceMappingURL=intelligentPhotoCaptureEstimatorImageAnalyzeSaga.js.map